// Trivia Night in VR: a panel that follows you around the venue (no chair) —
// Join / Host while you're not playing, the lobby settings, then the question
// with four huge colored answer pads (poke or pinch-ray: bare hands work),
// your result, the standings, and the host's show controls. Everything is a
// tap; nothing needs a controller or hover.

import React, { useState } from 'react';
import { Text } from '@react-three/drei';
import { tableAction } from '../gameStore';
import { joinOption, joinTable, leaveTable, seatedCount } from '../kit/LobbyKit';
import { VRBtn, VRRulesPanel, VRLeaderboardPanel, FloatingPanelMount, vrColors as colors, noRaycast, Surface } from '../kit/vrKit';
import type { VRPanelProps } from '../types';
import { COUNTS, TIMERS, TRIVIA_CATEGORIES, DEFAULT_OPTIONS, type TriviaOptions } from '../../../../supabase/functions/games/trivia-core.ts';
import { ANSWER_COLORS, ANSWER_DIM, LETTERS } from './triviaLayout';
import { categoryLabel, fmtPts, hostAnswer, hostMove, isKicked, myPick, sendAnswer, standings, triviaOf, useTimeLeft, type TriviaState } from './triviaClient';
import { sfx, isMuted, setMuted } from './triviaSounds';

const GOLD = '#ffd166';
const W = 0.64;

/** Short labels for the VR category toggles. */
const SHORT: Record<string, string> = {
  general: 'General', music: 'Music', hiphop: 'Hip-Hop & R&B', movies: 'Movies & TV', sports: 'Sports', science: 'Science',
  history: 'History', geography: 'Geography', blackhistory: 'Black History', food: 'Food & Drink', popculture: 'Pop & Tech',
};

/** Remembered across remounts (phase changes) so a hidden join card stays hidden. */
const ui = { joinHidden: false };

function AnswerPad({ i, text, x, y, state, onPress }: {
  i: number; text: string; x: number; y: number; state: 'live' | 'picked' | 'right' | 'dim' | 'idle'; onPress: () => void;
}) {
  const [hover, setHover] = useState(false);
  const w = 0.3, h = 0.1;
  const live = state === 'live';
  const color = state === 'dim' ? ANSWER_DIM[i] : ANSWER_COLORS[i];
  return (
    <group position={[x, y, 0.006]} name={`vr-trivia-answer-${LETTERS[i]}`}
      onPointerOver={(e) => { e.stopPropagation(); setHover(true); }} onPointerOut={() => setHover(false)}
      onClick={(e) => { e.stopPropagation(); if (live) onPress(); }}>
      {(state === 'right' || state === 'picked' || (live && hover)) && <Surface width={w + 0.012} height={h + 0.012} color={state === 'right' ? GOLD : '#ffffff'} r={0.016} z={-0.001} />}
      <Surface width={w} height={h} color={color} r={0.014} />
      <mesh raycast={noRaycast} position={[-w / 2 + 0.03, 0, 0.002]}>
        <circleGeometry args={[0.019, 24]} />
        <meshBasicMaterial color={state === 'dim' ? '#0b0f14' : '#ffffff'} toneMapped={false} />
      </mesh>
      <Text raycast={noRaycast} position={[-w / 2 + 0.03, 0, 0.004]} fontSize={0.022} color={state === 'dim' ? colors.muted : ANSWER_COLORS[i]}>{LETTERS[i]}</Text>
      <Text raycast={noRaycast} position={[-w / 2 + 0.06, 0, 0.004]} anchorX="left" fontSize={text.length > 28 ? 0.0135 : 0.016} maxWidth={w - 0.075} lineHeight={1.15}
        color={state === 'dim' ? colors.muted : '#ffffff'}>{text}</Text>
    </group>
  );
}

function JoinCard({ def, pid, entry, worldId }: Pick<VRPanelProps, 'def' | 'pid' | 'entry' | 'worldId'>) {
  const [hidden, setHidden] = useState(ui.joinHidden);
  const [rules, setRules] = useState(false);
  const [board, setBoard] = useState(false);
  const st = entry.table!.state;
  const join = joinOption(def, st);
  const canHost = !!def.host && def.host !== 'none' && !st.host && st.phase !== 'over';
  const hide = (v: boolean) => { ui.joinHidden = v; setHidden(v); };
  if (hidden) {
    return <VRBtn name="vr-trivia-show" label={`${def.glyph} Trivia`} x={0} y={0} w={0.16} h={0.045} fontSize={0.014} onPress={() => hide(false)} />;
  }
  const status = st.phase === 'lobby' ? `${seatedCount(st)} in · up to ${def.maxPlayers}` : st.phase === 'playing' ? 'Game on — jump in any time' : 'Game finished';
  return (
    <group>
      <group position={[0, -0.04, 0]}><Surface width={0.5} height={0.29} color={colors.panel} /></group>
      <Text raycast={noRaycast} position={[-0.23, 0.065, 0.004]} anchorX="left" fontSize={0.022} color={colors.text}>{`${def.glyph} ${def.label}`}</Text>
      <VRBtn name="vr-trivia-hide" label="Hide" x={0.19} y={0.065} w={0.09} h={0.04} fontSize={0.013} onPress={() => hide(true)} />
      <Text raycast={noRaycast} position={[0, 0.02, 0.004]} fontSize={0.014} color={colors.muted}>{`${status}${st.host ? ` · host ${st.host.name}` : ''}`}</Text>
      <VRBtn name="vr-game-play" label={st.phase === 'lobby' ? 'Join' : 'Join in'} x={canHost ? -0.085 : 0} y={-0.04} w={canHost ? 0.22 : 0.3} h={0.06}
        active={!!join} disabled={!join || entry.busy} onPress={() => void joinTable(pid, st)} />
      {canHost && <VRBtn name="vr-game-host" label="Host" x={0.155} y={-0.04} w={0.14} h={0.06} disabled={entry.busy} onPress={() => void tableAction(pid, 'host')} />}
      <VRBtn name="vr-game-howto" label="How to play" x={-0.12} y={-0.125} w={0.22} h={0.045} fontSize={0.014} active={rules} onPress={() => { setBoard(false); setRules((v) => !v); }} />
      <VRBtn name="vr-game-leaders" label="Leaders" x={0.12} y={-0.125} w={0.22} h={0.045} fontSize={0.014} active={board} onPress={() => { setRules(false); setBoard((v) => !v); }} />
      {rules && <group position={[0, 0, 0.02]}><VRRulesPanel def={def} y={0.13} onClose={() => setRules(false)} /></group>}
      {board && <group position={[0, 0.13 + 0.33, 0.02]}><VRLeaderboardPanel def={def} worldId={worldId} onClose={() => setBoard(false)} /></group>}
      {entry.error && <Text raycast={noRaycast} position={[0, -0.172, 0.004]} fontSize={0.012} color="#ff9b9b" maxWidth={0.48}>{entry.error}</Text>}
    </group>
  );
}

/** Lobby settings + start. Returns [node, height]. */
function lobbyBody({ def, pid, entry, table, isHost }: VRPanelProps): [React.ReactNode, number] {
  const st = table.state;
  const o: TriviaOptions = { ...DEFAULT_OPTIONS, ...(st.options ?? {}) };
  const canEdit = isHost || !st.host;
  const set = (patch: Partial<TriviaOptions>) => void tableAction(pid, 'configure', { options: patch });
  const n = seatedCount(st);
  const ready = n >= def.minPlayers;
  const dis = !canEdit || entry.busy;
  const cats = o.categories;
  const all = [{ id: '', label: 'Mix (all)' }, ...TRIVIA_CATEGORIES.map((c) => ({ id: c.id, label: SHORT[c.id] ?? c.label }))];
  const H = 0.6;
  const top = H / 2 - 0.07;
  const node = <>
    <Text raycast={noRaycast} position={[0, top, 0.004]} fontSize={0.016} color={colors.muted}>
      {`${n}/${def.maxPlayers} in${st.host ? ` · host ${st.host.name}` : ' · no host — the game runs itself'}${canEdit ? '' : ' · host picks the settings'}`}
    </Text>
    {all.map((c, k) => {
      const on = c.id === '' ? cats.length === 0 : cats.includes(c.id);
      const col = k % 3, row = Math.floor(k / 3);
      return <VRBtn key={c.id || 'mix'} name={`vr-trivia-cat-${c.id || 'mix'}`} label={c.label} x={-0.205 + col * 0.205} y={top - 0.05 - row * 0.052} w={0.195} h={0.045} fontSize={0.0125}
        active={on} disabled={dis} onPress={() => set({ categories: c.id === '' ? [] : on ? cats.filter((x) => x !== c.id) : [...cats, c.id] })} />;
    })}
    {COUNTS.map((c, k) => <VRBtn key={c} name={`vr-trivia-count-${c}`} label={`${c} Qs`} x={-0.25 + k * 0.105} y={top - 0.27} w={0.095} h={0.048} fontSize={0.014} active={o.count === c} disabled={dis} onPress={() => set({ count: c })} />)}
    {TIMERS.map((t, k) => <VRBtn key={t} name={`vr-trivia-timer-${t}`} label={`${t}s`} x={0.06 + k * 0.095} y={top - 0.27} w={0.085} h={0.048} fontSize={0.014} active={o.timer === t} disabled={dis} onPress={() => set({ timer: t })} />)}
    <VRBtn name="vr-trivia-mode-classic" label="Classic" x={-0.15} y={top - 0.33} w={0.2} h={0.048} fontSize={0.014} active={o.mode === 'classic'} disabled={dis} onPress={() => set({ mode: 'classic' })} />
    <VRBtn name="vr-trivia-mode-buzzer" label="Buzzer" x={0.06} y={top - 0.33} w={0.2} h={0.048} fontSize={0.014} active={o.mode === 'buzzer'} disabled={dis} onPress={() => set({ mode: 'buzzer' })} />
    <VRBtn name="vr-game-start" label={ready ? 'Start game' : `Waiting for players (${n}/${def.minPlayers})`} x={0} y={top - 0.42} w={0.4} h={0.06} fontSize={0.017} active={ready} disabled={!ready || entry.busy} onPress={() => void tableAction(pid, 'start')} />
  </>;
  return [node, H];
}

function StandingsRows({ g, seats, seat, top, limit }: { g: TriviaState; seats: (unknown | null)[]; seat: number; top: number; limit: number }) {
  const rows = standings(g, g.phase === 'over' ? undefined : seats).slice(0, limit);
  return <>
    {rows.map((r, k) => {
      const c = r.seat === seat ? colors.teal : colors.text;
      return (
        <group key={r.seat} position={[0, top - k * 0.042, 0.004]}>
          <Text raycast={noRaycast} position={[-W / 2 + 0.04, 0, 0]} anchorX="left" fontSize={0.018} color={k === 0 ? GOLD : c}>{`${r.rank}.`}</Text>
          <Text raycast={noRaycast} position={[-W / 2 + 0.09, 0, 0]} anchorX="left" fontSize={0.018} maxWidth={0.36} color={c}>{`${r.name.slice(0, 20)}${r.seat === seat ? ' (you)' : ''}`}</Text>
          <Text raycast={noRaycast} position={[W / 2 - 0.04, 0, 0]} anchorX="right" fontSize={0.018} color={c}>{fmtPts(r.score)}</Text>
        </group>
      );
    })}
  </>;
}

function HostBar({ g, pid, entry, table, y, onPlayers, players }: { g: TriviaState; pid: string; entry: VRPanelProps['entry']; table: VRPanelProps['table']; y: number; onPlayers: () => void; players: boolean }) {
  const [confirmEnd, setConfirmEnd] = useState(false);
  if (g.phase === 'over') return null;
  const paused = g.pausedLeft !== null;
  const seated = table.state.seats.filter((s) => s && !(s.userId && g.kicked.includes(s.userId))).length;
  return (
    <group position={[0, y, 0]}>
      <VRBtn name="vr-trivia-pause" label={paused ? 'Resume' : 'Pause'} x={-0.24} y={0} w={0.115} h={0.05} fontSize={0.014} active={paused} disabled={entry.busy} onPress={() => void hostMove(pid, { type: paused ? 'resume' : 'pause' })} />
      <VRBtn name="vr-trivia-skip" label={g.phase === 'question' ? 'Skip Q' : 'Next'} x={-0.12} y={0} w={0.115} h={0.05} fontSize={0.014} disabled={entry.busy} onPress={() => void hostMove(pid, { type: 'skip' })} />
      <VRBtn name="vr-trivia-reveal" label="Reveal" x={0} y={0} w={0.115} h={0.05} fontSize={0.014} disabled={entry.busy || g.phase !== 'question'} onPress={() => void hostMove(pid, { type: 'reveal' })} />
      <VRBtn name="vr-trivia-end" label={confirmEnd ? 'Really?' : 'End'} x={0.12} y={0} w={0.115} h={0.05} fontSize={0.014} active={confirmEnd} disabled={entry.busy}
        onPress={() => { if (confirmEnd) { setConfirmEnd(false); void hostMove(pid, { type: 'end' }); } else setConfirmEnd(true); }} />
      <VRBtn name="vr-trivia-players" label={`Players ${seated}`} x={0.245} y={0} w={0.12} h={0.05} fontSize={0.013} active={players} onPress={onPlayers} />
    </group>
  );
}

function KickList({ g, pid, entry, table, top }: { g: TriviaState; pid: string; entry: VRPanelProps['entry']; table: VRPanelProps['table']; top: number }) {
  const [page, setPage] = useState(0);
  const list = table.state.seats.map((s, i) => ({ s, i })).filter(({ s }) => s && !(s.userId && g.kicked.includes(s.userId)));
  const per = 6, pages = Math.max(1, Math.ceil(list.length / per));
  const p = Math.min(page, pages - 1);
  return (
    <group position={[0, 0, 0.03]} onClick={(e) => e.stopPropagation()}>
      <group position={[0, top - 0.2, 0]}><Surface width={W} height={0.42} color={colors.panel} /></group>
      <Text raycast={noRaycast} position={[-W / 2 + 0.03, top - 0.03, 0.004]} anchorX="left" fontSize={0.016} color={GOLD}>Remove a player</Text>
      {list.slice(p * per, p * per + per).map(({ s, i }, k) => (
        <group key={i} position={[0, top - 0.08 - k * 0.052, 0.004]}>
          <Text raycast={noRaycast} position={[-W / 2 + 0.03, 0, 0]} anchorX="left" fontSize={0.017} maxWidth={0.38} color={colors.text}>{`${s!.name.slice(0, 20)}  ${fmtPts(g.scores[i] ?? 0)}`}</Text>
          <VRBtn name={`vr-trivia-kick-${i}`} label="Remove" x={W / 2 - 0.08} y={0} w={0.12} h={0.044} fontSize={0.013} disabled={entry.busy} onPress={() => void hostMove(pid, { type: 'kick', seat: i })} />
        </group>
      ))}
      {pages > 1 && <>
        <VRBtn name="vr-trivia-kick-prev" label="◀" x={-0.06} y={top - 0.39} w={0.08} h={0.04} disabled={p === 0} onPress={() => setPage(p - 1)} />
        <VRBtn name="vr-trivia-kick-next" label="▶" x={0.06} y={top - 0.39} w={0.08} h={0.04} disabled={p >= pages - 1} onPress={() => setPage(p + 1)} />
      </>}
    </group>
  );
}

export function TriviaVRPanel(props: VRPanelProps) {
  const { def, pid, worldId, entry, table, seat, isHost } = props;
  const [rules, setRules] = useState(false);
  const [board, setBoard] = useState(false);
  const [players, setPlayers] = useState(false);
  const [muted, setM] = useState(isMuted());
  const st = table.state;
  const g = st.phase === 'lobby' ? null : triviaOf(table);
  const left = useTimeLeft(g);

  if (seat < 0 && !isHost) {
    return <FloatingPanelMount distance={0.55} drop={0.42}><JoinCard def={def} pid={pid} entry={entry} worldId={worldId} /></FloatingPanelMount>;
  }

  let body: React.ReactNode = null;
  let H = 0.5;
  let title = def.label;
  if (st.phase === 'lobby' || !g) {
    [body, H] = lobbyBody(props);
    title = isHost ? `${def.label} — you host` : def.label;
  } else {
    const kicked = seat >= 0 && isKicked(table, seat);
    const top = H / 2 - 0.075;
    const pick = myPick(g, entry.priv);
    const known = isHost ? hostAnswer(g, entry.priv) : null;
    const rv = g.phase === 'reveal' ? g.reveal : null;
    if (g.phase === 'intro') title = 'Get ready!';
    else if (g.phase === 'scores') title = 'Standings';
    else if (g.phase === 'over') title = 'Final scores';
    else title = `Question ${g.qNo} of ${g.total}`;

    if (kicked) {
      body = <Text raycast={noRaycast} position={[0, 0, 0.004]} fontSize={0.018} maxWidth={W - 0.06} textAlign="center" color={colors.muted}>The host removed you from this game. Watch the screen, or Leave.</Text>;
    } else if (g.phase === 'intro') {
      body = <>
        <Text raycast={noRaycast} position={[0, 0.04, 0.004]} fontSize={0.02} maxWidth={W - 0.06} textAlign="center" color={colors.text}>
          {`${g.total} questions · ${g.timerMs / 1000}s each · ${g.mode === 'buzzer' ? 'Buzzer: first right answer scores most' : 'Classic: answer fast for more points'}`}
        </Text>
        {left !== null && <Text raycast={noRaycast} position={[0, -0.07, 0.004]} fontSize={0.06} color={colors.teal}>{String(Math.max(1, Math.ceil(left / 1000)))}</Text>}
      </>;
    } else if ((g.phase === 'question' || g.phase === 'reveal') && g.question) {
      const live = !isHost && seat >= 0 && g.phase === 'question' && g.pausedLeft === null && pick === null && !entry.busy;
      const mine = rv && seat >= 0 ? rv.picks[seat] : undefined;
      let status = '';
      if (g.phase === 'question') status = g.pausedLeft !== null ? 'Paused by the host' : isHost ? `${g.answered.length} answered${known !== null ? ` · answer: ${LETTERS[known]}` : ''}` : pick !== null ? 'Locked in — wait for the reveal' : 'Tap your answer';
      else if (rv?.voided) status = 'Thrown out — no points';
      else if (rv && seat >= 0) status = mine === undefined ? `No answer — it was ${LETTERS[rv.correct]}` : mine === rv.correct ? `Right! +${fmtPts(rv.gains[seat] ?? 0)}${rv.fastest?.seat === seat ? ' — fastest!' : ''}` : `Wrong — it was ${LETTERS[rv.correct]}${rv.gains[seat] ? ` (${fmtPts(rv.gains[seat])})` : ''}`;
      else if (rv) status = rv.fastest ? `Fastest: ${g.names[rv.fastest.seat] ?? 'someone'}` : 'Nobody got it';
      body = <>
        <Text raycast={noRaycast} position={[0, top - 0.055, 0.004]} fontSize={g.question.text.length > 90 ? 0.0165 : 0.019} maxWidth={W - 0.05} textAlign="center" lineHeight={1.2} anchorY="middle" color={colors.text}>{g.question.text}</Text>
        {g.question.choices.map((c, i) => {
          const right = rv ? !rv.voided && rv.correct === i : known === i;
          const state = right ? 'right' : rv || pick !== null ? (pick === i ? 'picked' : 'dim') : live ? 'live' : 'idle';
          return <AnswerPad key={i} i={i} text={c} x={i % 2 === 0 ? -0.157 : 0.157} y={-0.035 - Math.floor(i / 2) * 0.112} state={state}
            onPress={() => { sfx.buzz(); void sendAnswer(pid, g.qNo, i); }} />;
        })}
        <Text raycast={noRaycast} position={[0, -H / 2 + 0.035, 0.004]} fontSize={0.016} maxWidth={W - 0.05} textAlign="center"
          color={rv && seat >= 0 && mine !== undefined ? (mine === rv.correct ? colors.teal : '#ff9b9b') : colors.muted}>{status}</Text>
      </>;
    } else {
      body = <>
        <StandingsRows g={g} seats={st.seats} seat={seat} top={top - 0.04} limit={g.phase === 'over' ? 6 : 8} />
        {st.phase === 'over' && <VRBtn name="vr-game-rematch" label="Rematch" x={0} y={-H / 2 + 0.045} w={0.26} h={0.055} active disabled={entry.busy} onPress={() => void tableAction(pid, 'rematch')} />}
      </>;
    }
    if (isHost && g.phase !== 'over') H += 0.07;
  }

  const sLeft = left === null ? null : Math.ceil(left / 1000);
  const me = g ? standings(g, st.seats).find((r) => r.seat === seat) : undefined;
  const headY = H / 2 - 0.032;
  return (
    <FloatingPanelMount distance={0.5} drop={0.3}>
      <Surface width={W} height={H} color={colors.panel} />
      <group position={[0, isHost && g && g.phase !== 'over' ? 0.035 : 0, 0]}>{body}</group>
      <Text raycast={noRaycast} position={[-W / 2 + 0.025, headY, 0.004]} anchorX="left" fontSize={0.016} maxWidth={0.26} color={colors.teal}>{title}</Text>
      {g && (
        <Text raycast={noRaycast} position={[-W / 2 + 0.025, headY - 0.04, 0.004]} anchorX="left" fontSize={0.014} maxWidth={W - 0.05}
          color={sLeft !== null && sLeft <= 5 && g.phase === 'question' ? '#ff9b9b' : colors.muted}>
          {[(g.phase === 'question' || g.phase === 'reveal') && g.question ? categoryLabel(g.question.category) : '',
            sLeft !== null && g.phase !== 'over' ? `${g.pausedLeft !== null ? 'paused ' : ''}${sLeft}s left` : '',
            me ? `${fmtPts(me.score)} pts · #${me.rank}` : ''].filter(Boolean).join('  ·  ')}
        </Text>
      )}
      <VRBtn name="vr-trivia-mute" label={muted ? 'Sound off' : 'Sound on'} x={W / 2 - 0.255} y={headY} w={0.075} h={0.04} fontSize={0.0105} onPress={() => { setMuted(!muted); setM(!muted); }} />
      <VRBtn name="vr-game-howto" label="?" x={W / 2 - 0.19} y={headY} w={0.045} h={0.04} fontSize={0.016} active={rules} onPress={() => { setBoard(false); setRules((v) => !v); }} />
      <VRBtn name="vr-game-leaders" label="Leaders" x={W / 2 - 0.12} y={headY} w={0.085} h={0.04} fontSize={0.013} active={board} onPress={() => { setRules(false); setBoard((v) => !v); }} />
      <VRBtn label={isHost ? 'Unhost' : 'Leave'} x={W / 2 - 0.04} y={headY} w={0.07} h={0.04} fontSize={0.012} onPress={() => leaveTable(pid)} />
      {isHost && g && <HostBar g={g} pid={pid} entry={entry} table={table} y={-H / 2 + 0.035} players={players} onPlayers={() => setPlayers((v) => !v)} />}
      {isHost && g && players && g.phase !== 'over' && <KickList g={g} pid={pid} entry={entry} table={table} top={H / 2 - 0.06} />}
      {rules && <group position={[0, 0, 0.03]}><VRRulesPanel def={def} y={-0.31} onClose={() => setRules(false)} /></group>}
      {board && <group position={[0, 0, 0.03]}><VRLeaderboardPanel def={def} worldId={worldId} refreshKey={st.gameId} onClose={() => setBoard(false)} /></group>}
      {entry.error && <Text raycast={noRaycast} position={[0, -H / 2 - 0.02, 0.004]} fontSize={0.013} maxWidth={W} color="#ff9b9b">{entry.error}</Text>}
    </FloatingPanelMount>
  );
}

export default TriviaVRPanel;
